import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, Users, DollarSign, CheckCircle, AlertTriangle, ChefHat, Car, Waves, Music, Eye, CreditCard, Phone, Filter, ChevronLeft, ChevronRight, X } from 'lucide-react';

const ConciergeManagement = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('all');

  // Mock data pour les statistiques
  const stats = {
    servicesToday: 12,
    servicesWeek: 47,
    servicesMonth: 186,
    pendingConfirmations: 8,
    activeSuppliers: 23,
    paymentsDue: "€15,670"
  };

  // Données pour la vue semaine
  const weekData = [
    { day: 'Lundi', date: '16/12', services: 8, revenue: '€3,250', status: 'active' },
    { day: 'Mardi', date: '17/12', services: 12, revenue: '€4,670', status: 'active' },
    { day: 'Mercredi', date: '18/12', services: 9, revenue: '€2,890', status: 'active' },
    { day: 'Jeudi', date: '19/12', services: 15, revenue: '€5,240', status: 'busy' },
    { day: 'Vendredi', date: '20/12', services: 18, revenue: '€6,830', status: 'busy' },
    { day: 'Samedi', date: '21/12', services: 22, revenue: '€8,950', status: 'peak' },
    { day: 'Dimanche', date: '22/12', services: 16, revenue: '€7,120', status: 'busy' }
  ];

  // Données pour la vue mois
  const monthData = [
    { week: 'Semaine 1', dates: '2-8 Dec', services: 45, revenue: '€18,670', growth: '+12%' },
    { week: 'Semaine 2', dates: '9-15 Dec', services: 52, revenue: '€22,340', growth: '+15%' },
    { week: 'Semaine 3', dates: '16-22 Dec', services: 67, revenue: '€28,950', growth: '+23%' },
    { week: 'Semaine 4', dates: '23-31 Dec', services: 89, revenue: '€35,670', growth: '+31%' }
  ];

  // Fonction pour naviguer dans les dates
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  // Fonction pour obtenir le titre de la date selon la vue
  const getDateTitle = () => {
    if (currentView === 'day') {
      return currentDate.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (currentView === 'week') {
      const startWeek = new Date(currentDate);
      const endWeek = new Date(currentDate);
      startWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
      endWeek.setDate(startWeek.getDate() + 6);
      return `Semaine du ${startWeek.getDate()}/${startWeek.getMonth() + 1} au ${endWeek.getDate()}/${endWeek.getMonth() + 1}`;
    } else {
      return currentDate.toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long'
      });
    }
  };

  // Fonction pour obtenir les statistiques selon la vue
  const getCurrentStats = () => {
    if (currentView === 'day') return stats.servicesToday;
    if (currentView === 'week') return stats.servicesWeek;
    return stats.servicesMonth;
  };

  // Services programmés
  const scheduledServices = [
    {
      id: 1,
      time: "09:00",
      duration: 180,
      type: "Chef",
      service: "Petit-déjeuner gastronomique",
      client: "Sophie Martin",
      property: "Villa Azure",
      supplier: "Chef Laurent Dubois",
      cost: 450,
      status: "confirmed",
      icon: <ChefHat size={16} />
    },
    {
      id: 2,
      time: "14:00",
      duration: 120,
      type: "Spa",
      service: "Massage couple",
      client: "Jean-Marc Dubois",
      property: "Château Provence",
      supplier: "Zen Spa Services",
      cost: 380,
      status: "pending",
      icon: <Users size={16} />
    },
    {
      id: 3,
      time: "16:00",
      duration: 240,
      type: "Transport",
      service: "Héli-transfer Monaco",
      client: "Isabella Romano",
      property: "Villa Toscane",
      supplier: "Monaco Helicopters",
      cost: 1200,
      status: "confirmed",
      icon: <Car size={16} />
    },
    {
      id: 4,
      time: "19:30",
      duration: 300,
      type: "Activities",
      service: "Dîner privé yacht",
      client: "Alexandre Martin",
      property: "Port de Cannes",
      supplier: "Luxury Yachts Riviera",
      cost: 2500,
      status: "confirmed",
      icon: <Waves size={16} />
    }
  ];

  // Services en cours
  const currentServices = [
    {
      id: 1,
      client: "Sophie Martin",
      service: "Chef privé - Dîner gastronomique",
      supplier: "Chef Laurent Dubois",
      cost: "€850",
      commission: "€170",
      status: "active",
      paymentStatus: "pending"
    },
    {
      id: 2,
      client: "Jean-Marc Dubois",
      service: "Guide œnologique privé",
      supplier: "Wine Expert Provence",
      cost: "€420",
      commission: "€84",
      status: "completed",
      paymentStatus: "paid"
    },
    {
      id: 3,
      client: "Isabella Romano",
      service: "Organisation événement privé",
      supplier: "Elite Events Monaco",
      cost: "€3,500",
      commission: "€700",
      status: "active",
      paymentStatus: "pending"
    }
  ];

  // Fournisseurs
  const suppliers = [
    {
      id: 1,
      name: "Chef Laurent Dubois",
      category: "Gastronomie",
      amountDue: "€2,450",
      commission: "€490",
      paymentStatus: "pending",
      dueDate: "2024-09-20",
      services: 8
    },
    {
      id: 2,
      name: "Monaco Helicopters",
      category: "Transport",
      amountDue: "€4,800",
      commission: "€960",
      paymentStatus: "paid",
      dueDate: "2024-09-15",
      services: 3
    },
    {
      id: 3,
      name: "Zen Spa Services",
      category: "Bien-être",
      amountDue: "€1,680",
      commission: "€336",
      paymentStatus: "pending",
      dueDate: "2024-09-25",
      services: 12
    }
  ];

  const financialSummary = {
    revenue: "€47,230",
    costs: "€32,150",
    commission: "€6,430",
    paymentsDue: "€15,670",
    paymentsReceivable: "€8,920"
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const ServiceModal = () => {
    if (!showServiceModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Nouveau Service Conciergerie</h3>
            <button
              onClick={() => setShowServiceModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>Sophie Martin - Villa Azure</option>
                  <option>Jean-Marc Dubois - Château Provence</option>
                  <option>Isabella Romano - Villa Toscane</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de Service</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>Chef Privé</option>
                  <option>Transport</option>
                  <option>Spa & Bien-être</option>
                  <option>Activités</option>
                  <option>Événement</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Heure</label>
                  <input
                    type="time"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fournisseur</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>Chef Laurent Dubois</option>
                  <option>Monaco Helicopters</option>
                  <option>Zen Spa Services</option>
                  <option>Elite Events Monaco</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Durée (minutes)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="120"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prix (€)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="450"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Commission (%)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instructions Spéciales</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  rows="4"
                  placeholder="Détails spéciaux pour ce service..."
                ></textarea>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowServiceModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                // Logique de sauvegarde
                setShowServiceModal(false);
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Créer le Service
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="concierge-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion Conciergerie</h1>
            <p className="text-gray-600 mt-1">Planifiez et gérez tous les services conciergerie pour vos clients VIP</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {['day', 'week', 'month'].map((view) => (
                <button
                  key={view}
                  onClick={() => setCurrentView(view)}
                  className={`px-3 py-2 rounded-md text-sm capitalize ${
                    currentView === view ? 'bg-white text-gray-800 shadow' : 'text-gray-600'
                  }`}
                >
                  {view === 'day' ? 'Jour' : view === 'week' ? 'Semaine' : 'Mois'}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowServiceModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <Plus size={18} />
              <span>Nouveau Service</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Alerte */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-orange-600" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">
                8 confirmations en attente nécessitent votre attention
              </p>
              <p className="text-sm text-orange-700">
                Vérifiez les services programmés pour demain et confirmez avec les fournisseurs.
              </p>
            </div>
            <button className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700">
              Voir les détails
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-blue-600">
                {currentView === 'day' ? 'Aujourd\'hui' : 
                 currentView === 'week' ? 'Cette semaine' : 'Ce mois'}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{getCurrentStats()}</div>
            <div className="text-sm text-gray-600">Services</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">
                {currentView === 'day' ? 'Prochaine heure' : 
                 currentView === 'week' ? 'Demain' : 'Semaine prochaine'}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">
              {currentView === 'day' ? '3' : currentView === 'week' ? '15' : '67'}
            </div>
            <div className="text-sm text-gray-600">Services</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-orange-600" size={24} />
              <span className="text-sm font-medium text-orange-600">En attente</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.pendingConfirmations}</div>
            <div className="text-sm text-gray-600">Confirmations</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-600">Actifs</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeSuppliers}</div>
            <div className="text-sm text-gray-600">Fournisseurs</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-red-600" size={24} />
              <span className="text-sm font-medium text-red-600">
                {currentView === 'day' ? 'Aujourd\'hui' : 
                 currentView === 'week' ? 'Cette semaine' : 'Ce mois'}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">
              {currentView === 'day' ? '€3,450' : 
               currentView === 'week' ? '€28,950' : '€105,630'}
            </div>
            <div className="text-sm text-gray-600">Revenus</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Planning Vue */}
          <div className="lg:col-span-2 space-y-6">
            {/* Navigation date */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigateDate('prev')}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {getDateTitle()}
                  </h3>
                  <button 
                    onClick={() => navigateDate('next')}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-sm font-medium text-blue-600">
                      {getCurrentStats()} services prévus
                    </span>
                  </div>
                  <select 
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="all">Tous les services</option>
                    <option value="Chef">Chef</option>
                    <option value="Spa">Spa</option>
                    <option value="Transport">Transport</option>
                    <option value="Activities">Activités</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contenu selon la vue sélectionnée */}
            {currentView === 'day' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Planning du Jour</h3>
                
                <div className="space-y-4">
                  {scheduledServices.map((service) => (
                    <div key={service.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-800">{service.time}</div>
                        <div className="text-sm text-gray-600">{service.duration}min</div>
                      </div>
                      
                      <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 text-yellow-600 rounded-lg">
                        {service.icon}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-800">{service.service}</h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                            {service.status === 'confirmed' ? 'Confirmé' : 'En attente'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{service.client} • {service.property}</p>
                        <p className="text-sm text-blue-600">{service.supplier} • €{service.cost}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-gray-600">
                          <Eye size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Phone size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentView === 'week' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Planning de la Semaine</h3>
                
                <div className="grid grid-cols-7 gap-4">
                  {weekData.map((day, index) => (
                    <div key={index} className={`p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                      day.status === 'peak' ? 'border-red-200 bg-red-50' :
                      day.status === 'busy' ? 'border-orange-200 bg-orange-50' :
                      'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="text-center">
                        <h4 className="font-semibold text-gray-800 text-sm">{day.day}</h4>
                        <p className="text-xs text-gray-600 mb-3">{day.date}</p>
                        
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                          day.status === 'peak' ? 'bg-red-100 text-red-600' :
                          day.status === 'busy' ? 'bg-orange-100 text-orange-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          <span className="text-lg font-bold">{day.services}</span>
                        </div>
                        
                        <p className="text-xs text-gray-600">Services</p>
                        <p className="text-sm font-semibold text-green-600 mt-1">{day.revenue}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-green-800">Total Semaine</h4>
                      <Calendar className="text-green-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-green-600 mt-2">100 services</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-blue-800">Revenus</h4>
                      <DollarSign className="text-blue-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mt-2">€38,950</p>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-orange-800">Taux occupation</h4>
                      <Users className="text-orange-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-orange-600 mt-2">82%</p>
                  </div>
                </div>
              </div>
            )}

            {currentView === 'month' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Vue Mensuelle</h3>
                
                <div className="space-y-4">
                  {monthData.map((week, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div>
                        <h4 className="font-semibold text-gray-800">{week.week}</h4>
                        <p className="text-sm text-gray-600">{week.dates}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{week.services}</p>
                        <p className="text-sm text-gray-600">Services</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{week.revenue}</p>
                        <p className="text-sm text-gray-600">Revenus</p>
                      </div>
                      
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${
                          week.growth.startsWith('+') ? 'text-green-600' : 'text-red-600'
                        }`}>{week.growth}</p>
                        <p className="text-sm text-gray-600">Croissance</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-purple-800">Total Mois</h4>
                      <Calendar className="text-purple-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-purple-600 mt-2">253 services</p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-green-800">Revenus Total</h4>
                      <DollarSign className="text-green-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-green-600 mt-2">€105,630</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-blue-800">Croissance</h4>
                      <CheckCircle className="text-blue-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mt-2">+18%</p>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-orange-800">Clients Uniques</h4>
                      <Users className="text-orange-600" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-orange-600 mt-2">47</p>
                  </div>
                </div>
              </div>
            )}

            {/* Services actuels */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Services en Cours</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="pb-3">Client</th>
                      <th className="pb-3">Service</th>
                      <th className="pb-3">Fournisseur</th>
                      <th className="pb-3">Coût</th>
                      <th className="pb-3">Commission</th>
                      <th className="pb-3">Statut</th>
                      <th className="pb-3">Paiement</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentServices.map((service) => (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="py-4 text-sm font-medium text-gray-900">{service.client}</td>
                        <td className="py-4 text-sm text-gray-900">{service.service}</td>
                        <td className="py-4 text-sm text-gray-900">{service.supplier}</td>
                        <td className="py-4 text-sm font-medium text-gray-900">{service.cost}</td>
                        <td className="py-4 text-sm font-medium text-green-600">{service.commission}</td>
                        <td className="py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                            {service.status === 'active' ? 'Actif' : 'Terminé'}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(service.paymentStatus)}`}>
                            {service.paymentStatus === 'paid' ? 'Payé' : 'En attente'}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex items-center space-x-2">
                            <button className="text-gray-400 hover:text-gray-600">
                              <Eye size={16} />
                            </button>
                            <button className="text-gray-400 hover:text-green-600">
                              <CreditCard size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar droite */}
          <div className="space-y-6">
            {/* Résumé financier */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Résumé Financier</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Revenus</span>
                  <span className="font-medium text-green-600">{financialSummary.revenue}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coûts</span>
                  <span className="font-medium text-red-600">{financialSummary.costs}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Commission PRIVIA</span>
                  <span className="font-medium text-blue-600">{financialSummary.commission}</span>
                </div>
                <hr />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Paiements dus</span>
                  <span className="font-medium text-orange-600">{financialSummary.paymentsDue}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">À recevoir</span>
                  <span className="font-medium text-green-600">{financialSummary.paymentsReceivable}</span>
                </div>
              </div>
            </div>

            {/* Fournisseurs */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Fournisseurs</h3>
              
              <div className="space-y-4">
                {suppliers.map((supplier) => (
                  <div key={supplier.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-gray-800">{supplier.name}</h4>
                        <p className="text-sm text-gray-600">{supplier.category}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(supplier.paymentStatus)}`}>
                        {supplier.paymentStatus === 'paid' ? 'Payé' : 'En attente'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">Montant dû:</span>
                        <span className="font-medium text-gray-800 ml-1">{supplier.amountDue}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Commission:</span>
                        <span className="font-medium text-green-600 ml-1">{supplier.commission}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Services:</span>
                        <span className="font-medium text-gray-800 ml-1">{supplier.services}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Échéance:</span>
                        <span className="font-medium text-gray-800 ml-1">{supplier.dueDate}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                        Payer
                      </button>
                      <button className="text-sm text-gray-600 hover:text-gray-700 font-medium">
                        Voir facture
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ServiceModal />
    </div>
  );
};

export default ConciergeManagement;